import useAuthStore from '~/stores/auth';
import { isLoadingTitle, titleObserverConfig } from '~/functions/isLoadingTitle';

// Define Gtag type based on nuxt-gtag types
interface GtagCommands {
   config: [targetId: string, config?: any];
   set: [targetId: string, config: any] | [config: any];
   event: [eventName: string, eventParams?: any];
   get: [targetId: string, fieldName: string, callback?: (field?: any) => any];
}

interface Gtag {
   <Command extends keyof GtagCommands>(command: Command, ...args: GtagCommands[Command]): void;
}

declare global {
   interface Window {
      [key: string]: any;
   }
}

export default defineNuxtPlugin((nuxtApp: any) => {
   const config = useRuntimeConfig();
   const authStore = useAuthStore();

   const getCorporateDomains = () => {
      const domainsString = config.public.corporateDomains as string;
      return domainsString ? domainsString.split('|').map((d) => d.trim()) : [];
   };

   const isInternalUser = () => {
      const email = authStore?.user?.user?.email;
      if (!email) return false;

      const corporateDomains = getCorporateDomains();
      return corporateDomains.some((domain) =>
         email.toLowerCase().endsWith(`@${domain.toLowerCase()}`),
      );
   };

   const checkAndDisableGA = () => {
      // Check if GA is disabled locally
      if (
         process.env.NODE_ENV === 'development' &&
         !config.public.enableGALocally
      ) {
         console.info('GA disabled for local development');
         return true;
      }

      if (isInternalUser()) {
         // Disable all GA tracking
         const gtagId = (config.public as any).gtag?.id || 'G-MJZTC6T38T';
         window[`ga-disable-${gtagId}`] = true;
         console.info('GA completely disabled for internal user');
         return true;
      }
      return false;
   };

   const sendPortalInfo = () => {
      // Don't do anything if GA is disabled
      if (checkAndDisableGA()) return;

      const portal = localStorage.getItem('portal');
      const { gtag }: { gtag: Gtag } = useGtag();

      if (gtag && portal) {
         gtag('set', 'user_properties', { portal });
      }
   };

   // Wait for app to be mounted to ensure gtag is available
   nuxtApp.hook('app:mounted', () => {
      sendPortalInfo();
   });


   // Universal function to track page view when title is ready
   const trackPageViewWhenReady = () => {
      const { gtag }: { gtag: Gtag } = useGtag();
      if (!gtag || checkAndDisableGA()) return;

      const currentRoute = nuxtApp.$router.currentRoute.value;
      const queryParams = Object.entries(currentRoute.query || {}).reduce((acc, [key, value]) => {
         if (value !== null && value !== undefined) {
            acc[key] = Array.isArray(value) ? value[0] : String(value);
         }
         return acc;
      }, {} as Record<string, string>);
      const queryString = Object.keys(queryParams).length > 0 ? new URLSearchParams(queryParams).toString() : '';
      const pathWithQuery = queryString ? `${currentRoute.path}?${queryString}` : currentRoute.path;

      let hasTracked = false;
      let attempts = 0;
      const maxAttempts = 50; // Poll for 5 seconds max (50 * 100ms)
      const initialTitle = document.title; // Capture stale title from previous page

      const doTrack = (title: string) => {
         if (hasTracked) return;
         hasTracked = true;
         gtag('event', 'page_view', {
            page_title: title,
            page_location: window.location.href,
            page_path: pathWithQuery
         });
      };

      // Poll for title changes using intervals
      const pollForTitle = () => {
         const currentTitle = document.title;
         attempts++;

         const titleChanged = currentTitle !== initialTitle;
         const titleReady = !isLoadingTitle(currentTitle);
         const initialWasLoading = isLoadingTitle(initialTitle);

         // Track when title is not a loading state AND either:
         // - Title changed from previous page, OR
         // - Initial title was a loading state, OR
         // - First poll and title is already ready (static page where title is set before page:finish)
         if (titleReady && (titleChanged || initialWasLoading || attempts === 1)) {
            doTrack(currentTitle);
            return;
         }

         // Continue polling if we haven't exceeded max attempts
         if (attempts < maxAttempts) {
            setTimeout(pollForTitle, 100);
         }
      };

      // Start polling immediately
      pollForTitle();
   };

   // Set on every route change
   nuxtApp.hook('page:finish', () => {
      sendPortalInfo();

      // Use universal tracking for ALL pages - no special cases
      trackPageViewWhenReady();
   });
});
